import React, { createContext, useState, useEffect, useRef, useCallback } from "react";

export const AuthContext = createContext();

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
const LAST_ACTIVITY_STORAGE_KEY = "authLastActivityAt";

function clearStoredAuth() {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("userId");
  localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
}

function getStoredUser() {
  const storedUser = localStorage.getItem("user");
  if (!storedUser) {
    return null;
  }

  try {
    return JSON.parse(storedUser);
  } catch {
    localStorage.removeItem("user");
    return null;
  }
}

function readJwtPayload(token) {
  if (!token) {
    return null;
  }

  try {
    const [, payload] = token.split(".");
    if (!payload) {
      return null;
    }

    const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedPayload = normalizedPayload.padEnd(normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4), "=");
    return JSON.parse(window.atob(paddedPayload));
  } catch {
    return null;
  }
}

function getStoredLastActivityAt() {
  const value = Number(localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY));
  return Number.isFinite(value) ? value : null;
}

function persistLastActivity(timestamp = Date.now()) {
  localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(timestamp));
}

function isSessionExpired(token) {
  if (!token) {
    return false;
  }

  const now = Date.now();
  const payload = readJwtPayload(token);
  const tokenExpiryAt = payload?.exp ? payload.exp * 1000 : null;
  if (tokenExpiryAt && tokenExpiryAt <= now) {
    return true;
  }

  const lastActivityAt = getStoredLastActivityAt();
  if (lastActivityAt) {
    return now - lastActivityAt >= INACTIVITY_TIMEOUT_MS;
  }

  const issuedAt = payload?.iat ? payload.iat * 1000 : null;
  if (issuedAt) {
    return now - issuedAt >= INACTIVITY_TIMEOUT_MS;
  }

  return false;
}

function getInitialAuthState() {
  const storedToken = localStorage.getItem("token");
  if (!storedToken || isSessionExpired(storedToken)) {
    clearStoredAuth();
    return { token: null, user: null };
  }

  return {
    token: storedToken,
    user: getStoredUser(),
  };
}

function requestHasAuthorizationHeader(input, init) {
  const initHeaders = new Headers(init?.headers || undefined);
  if (initHeaders.has("Authorization")) {
    return true;
  }

  if (typeof Request !== "undefined" && input instanceof Request) {
    return input.headers.has("Authorization");
  }

  return false;
}

export function AuthProvider({ children }) {
  const [authState, setAuthState] = useState(() => getInitialAuthState());
  // Add refresh state for dashboard analytics
  const [refresh, setRefresh] = useState(0);
  const inactivityTimeoutRef = useRef(null);
  const token = authState.token;
  const user = authState.user;

  const setUser = useCallback((value) => {
    setAuthState((current) => {
      const nextUser = typeof value === "function" ? value(current.user) : value;

      if (nextUser) {
        localStorage.setItem("user", JSON.stringify(nextUser));
        if (nextUser._id) {
          localStorage.setItem("userId", nextUser._id);
        }
      } else {
        localStorage.removeItem("user");
        localStorage.removeItem("userId");
      }

      return {
        ...current,
        user: nextUser,
      };
    });
  }, []);

  const syncSession = useCallback((session) => {
    const nextUser = session?.user || session || null;
    const nextToken = session?.token || null;

    if (nextToken) {
      localStorage.setItem("token", nextToken);
    } else {
      localStorage.removeItem("token");
    }

    if (nextUser) {
      localStorage.setItem("user", JSON.stringify(nextUser));
      if (nextUser._id) {
        localStorage.setItem("userId", nextUser._id);
      }
    } else {
      localStorage.removeItem("user");
      localStorage.removeItem("userId");
    }

    if (nextToken) {
      persistLastActivity();
    } else {
      localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
    }

    setAuthState({
      token: nextToken,
      user: nextUser,
    });
  }, []);

  const login = (loginResponse) => {
    syncSession(loginResponse);
    console.log("AuthContext login setUser:", loginResponse.user || loginResponse);
  };

  const logout = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
    clearStoredAuth();
    setAuthState({ token: null, user: null });
    console.log("AuthContext logout setUser: null");
  }, []);

  const validateSession = useCallback(() => {
    if (!token) {
      return false;
    }

    if (isSessionExpired(token)) {
      logout();
      return false;
    }

    return true;
  }, [logout, token]);

  const resetInactivityTimer = useCallback(() => {
    if (!token) {
      return;
    }

    persistLastActivity();

    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }
    inactivityTimeoutRef.current = setTimeout(() => {
      logout();
    }, INACTIVITY_TIMEOUT_MS);
  }, [logout, token]);

  useEffect(() => {
    if (!token) {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
      return undefined;
    }

    if (!validateSession()) {
      return undefined;
    }

    const activityEvents = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    const handleUserActivity = () => {
      resetInactivityTimer();
    };
    const handleWindowFocus = () => {
      validateSession();
    };
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        validateSession();
      }
    };

    resetInactivityTimer();
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleUserActivity, { passive: true });
    });
    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleUserActivity);
      });
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
    };
  }, [resetInactivityTimer, token, validateSession]);

  useEffect(() => {
    const nativeFetch = window.fetch.bind(window);

    window.fetch = async (input, init) => {
      const response = await nativeFetch(input, init);

      if (response.status === 401 && requestHasAuthorizationHeader(input, init)) {
        logout();
      }

      return response;
    };

    return () => {
      window.fetch = nativeFetch;
    };
  }, [logout]);

  // Debug: log user whenever setUser is called
  useEffect(() => {
    console.log("AuthContext user updated:", user);
  }, [user]);

  return (
    <AuthContext.Provider value={{ token, user, login, logout, refresh, setRefresh, setUser, syncSession }}>
      {children}
    </AuthContext.Provider>
  );
}